import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Select, Button, message, Modal, Radio, InputNumber, Checkbox, Space } from "antd";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Toolbar,
  Filter,
  Sort,
  Selection,
} from "@syncfusion/ej2-react-grids";
import dayjs from "dayjs";
import withMosadIsolation from "../../utils/withMosadIsolation";
import { fetchTransactions, exportMasavFile, markTransactionsExported } from "../../redux/slices/transactionsSlice";
import { fetchCustomers } from "../../redux/slices/customersSlice";
import { useTranslation } from "../../i18n";
import { exportTransactionsToRivhit } from "../../utils/exportRivhit";
import { printTable } from "../../utils/printTable";

const { Option } = Select;

const Transactions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const transactions = useSelector((state) => state.transactions.transactionsList);
  const customers = useSelector((state) => state.customers.customersList);

  const gridRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [chargeFromDate, setChargeFromDate] = useState(null);
  const [chargeToDate, setChargeToDate] = useState(null);

  const [createFromDate, setCreateFromDate] = useState(null);
  const [createToDate, setCreateToDate] = useState(null);

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [gridData, setGridData] = useState([]);

  const [rivhitModalOpen, setRivhitModalOpen] = useState(false);
  const [dueDateMode, setDueDateMode] = useState("charge_date");
  const [customDueDate, setCustomDueDate] = useState(dayjs());
  const [sortCode, setSortCode] = useState(100);
  const [includeVat, setIncludeVat] = useState(true);
  const [vatPercent, setVatPercent] = useState(18);

  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedSum, setSelectedSum] = useState(0);
  const [useExternNumber, setUseExternNumber] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!transactions || transactions.length === 0) {
          await dispatch(fetchTransactions());
        }
        if (!customers || customers.length === 0) {
          await dispatch(fetchCustomers());
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [dispatch]);

  function formatDateDdMmYyyy(value) {
    if (value === null || value === undefined || value === "") return "";
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function dateValueAccessor(field, data) {
    return formatDateDdMmYyyy(data?.[field]);
  }

  const compareDate = (a, b) => {
    const av = dayjs(a).valueOf();
    const bv = dayjs(b).valueOf();
    return av - bv;
  };

  const mapToGridRows = (list) =>
    list.map((t) => {
      const customerEntity = customers?.find(
        (c) => String(c.number) === String(t.customer)
      );
      return {
        asc_num: t.asc_num,
        customer: customerEntity?.number || t.customer,
        name: customerEntity?.name || t.name,
        id: customerEntity?.id || "",
        cust_extern: Number(customerEntity?.cust_extern) || 0,
        charge_sum: Number(t.charge_sum) || 0,
        period: t.period || "",
        charge_date: t.charge_date,
        create_date: t.create_date,
        note: t.note ? t.note : "",
      };
    });

  const handleSearch = () => {
    if (!transactions || transactions.length === 0) {
      message.error(t("notifications.frontend.common.noTransactionsAvailable"));
      return;
    }

    const hasChargeFilter = chargeFromDate || chargeToDate;
    const hasCreateFilter = createFromDate || createToDate;
    const hasCustomerFilter = !!selectedCustomer;

    if (!hasChargeFilter && !hasCreateFilter && !hasCustomerFilter) {
      message.warning(t("ui.transactions.selectAtLeastOneFilter"));
      return;
    }

    let filtered = [...transactions];

    if (chargeFromDate) {
      const start = chargeFromDate.startOf("day");
      filtered = filtered.filter(
        (t) => dayjs(t.charge_date).valueOf() >= start.valueOf()
      );
    }
    if (chargeToDate) {
      const end = chargeToDate.endOf("day");
      filtered = filtered.filter(
        (t) => dayjs(t.charge_date).valueOf() <= end.valueOf()
      );
    }

    if (createFromDate) {
      const start = createFromDate.startOf("day");
      filtered = filtered.filter(
        (t) => dayjs(t.create_date).valueOf() >= start.valueOf()
      );
    }
    if (createToDate) {
      const end = createToDate.endOf("day");
      filtered = filtered.filter(
        (t) => dayjs(t.create_date).valueOf() <= end.valueOf()
      );
    }

    if (selectedCustomer) {
      filtered = filtered.filter(
        (t) => String(t.customer) === String(selectedCustomer)
      );
    }

    const result = mapToGridRows(filtered);
    const sorted = result.sort((a, b) => Number(b.asc_num) - Number(a.asc_num));
    gridRef.current?.clearSelection?.();
    setSelectedCount(0);
    setSelectedSum(0);
    setGridData(sorted);

    if (sorted.length === 0) {
      message.info(t("ui.transactions.noResults"));
    }
  };

  const getSelectedRows = () => {
    const grid = gridRef.current;
    return grid?.getSelectedRecords?.() || [];
  };

  const updateSelectedSummary = () => {
    const selected = getSelectedRows();
    setSelectedCount(selected.length);
    setSelectedSum(
      selected.reduce((acc, row) => acc + (Number(row.charge_sum) || 0), 0)
    );
  };

  const handleSelectionChange = () => {
    setTimeout(updateSelectedSummary, 0);
  };

  const formatSum = (value) =>
    (Number(value) || 0).toLocaleString("he-IL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handlePrintSelected = () => {
    const selected = getSelectedRows();
    if (!selected.length) {
      message.warning(t("ui.transactions.selectRowsForPrint"));
      return;
    }

    const totalSum = selected.reduce(
      (acc, row) => acc + (Number(row.charge_sum) || 0),
      0
    );
    const ok = printTable({
      title: t("ui.transactions.title"),
      summary: t("ui.transactions.selectedMovesSummary", {
        count: selected.length,
        sum: formatSum(totalSum),
      }),
      columns: [
        { header: t("ui.transactions.asmakhta"), format: (r) => r.asc_num ?? "" },
        { header: t("ui.transactions.customerNumber"), format: (r) => r.customer ?? "" },
        { header: t("ui.transactions.name"), format: (r) => r.name ?? "" },
        { header: t("ui.customers.id"), format: (r) => r.id ?? "" },
        { header: t("ui.transactions.chargeSum"), format: (r) => formatSum(r.charge_sum) },
        { header: t("ui.transactions.chargeDate"), format: (r) => formatDateDdMmYyyy(r.charge_date) },
      ],
      rows: selected,
    });
    if (!ok) message.error(t("ui.billing.printPopupBlocked"));
  };

  const handleExportRivhit = () => {
    if (!gridData || gridData.length === 0) {
      message.warning(t("ui.transactions.noResults"));
      return;
    }

    const selected = getSelectedRows();
    if (!selected.length) {
      message.warning(t("ui.transactions.selectRowsForRivhit"));
      return;
    }

    setDueDateMode("charge_date");
    setCustomDueDate(dayjs());
    setSortCode(100);
    setIncludeVat(true);
    setVatPercent(18);
    setRivhitModalOpen(true);
  };

  const downloadMasavBlob = (blob) => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const HH = String(now.getHours()).padStart(2, "0");
    const MM = String(now.getMinutes()).padStart(2, "0");
    const fileName = `MSV${yy}${mm}${dd}${HH}${MM}.001`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const runMasavExport = async (ascNums, chargeDate) => {
    try {
      setLoading(true);
      const blob = await dispatch(
        exportMasavFile({
          asc_nums: ascNums,
          charge_date: chargeDate
            ? dayjs(chargeDate).format("YYYY-MM-DD")
            : undefined,
          use_extern_number: useExternNumber,
        })
      ).unwrap();

      downloadMasavBlob(blob);
      message.success(t("notifications.frontend.common.actionsSavedAndFileDownloaded"));
      await dispatch(fetchTransactions());
    } catch (err) {
      console.error("MASAV export failed:", err);
      message.error(t("ui.transactions.masavExportFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleBuildMasav = async () => {
    if (!gridData || gridData.length === 0) {
      message.warning(t("ui.transactions.noResults"));
      return;
    }

    const selected = getSelectedRows();
    if (!selected.length) {
      message.warning(t("ui.transactions.selectRowsForMasav"));
      return;
    }

    const ascNums = selected
      .map((row) => row.asc_num)
      .filter((v) => v != null)
      .map((v) => String(v));
    if (!ascNums.length) {
      message.warning(t("ui.transactions.selectRowsForMasav"));
      return;
    }

    const firstChargeDate = selected[0]?.charge_date;
    const uniqueChargeDates = new Set(
      selected.map((row) => dayjs(row.charge_date).format("YYYY-MM-DD"))
    );

    if (uniqueChargeDates.size > 1) {
      Modal.confirm({
        title: t("ui.transactions.masavDifferentChargeDatesTitle"),
        content: t("ui.transactions.masavDifferentChargeDatesConfirm"),
        okText: t("common.yes"),
        cancelText: t("common.no"),
        onOk: () => runMasavExport(ascNums, firstChargeDate),
      });
      return;
    }

    await runMasavExport(ascNums, firstChargeDate);
  };

  const handleConfirmRivhitExport = async () => {
    if (dueDateMode === "custom" && !customDueDate) {
      message.warning(t("ui.transactions.rivhitSelectDueDate"));
      return;
    }

    const code = Number(sortCode);
    if (!Number.isFinite(code) || code < 100 || code > 999) {
      message.warning(t("ui.transactions.rivhitSortCodeInvalid"));
      return;
    }

    if (!includeVat) {
      const vat = Number(vatPercent);
      if (!Number.isFinite(vat) || vat < 0) {
        message.warning(t("ui.transactions.rivhitVatInvalid"));
        return;
      }
    }

    const selected = getSelectedRows();
    if (!selected.length) {
      message.warning(t("ui.transactions.selectRowsForRivhit"));
      return;
    }

    const ok = exportTransactionsToRivhit(selected, {
      dueDateMode,
      customDueDate: dueDateMode === "custom" ? customDueDate?.toDate?.() || customDueDate : null,
      sortCode: code,
      includeVat,
      vatPercent: Number(vatPercent) || 18,
    });

    if (!ok) {
      message.warning(t("ui.transactions.rivhitExportEmpty"));
      return;
    }

    // Only rows with positive charge_sum are written into the Rivhit file
    const exportedAscNums = selected
      .filter((row) => Number(row.charge_sum) > 0)
      .map((row) => row.asc_num)
      .filter((v) => v != null)
      .map((v) => String(v));

    try {
      if (exportedAscNums.length) {
        await dispatch(markTransactionsExported({ asc_nums: exportedAscNums })).unwrap();
      }
      message.success(t("ui.transactions.rivhitExportSuccess"));
      setRivhitModalOpen(false);
    } catch (error) {
      message.error(
        error?.message || t("notifications.frontend.common.unknownError")
      );
    }
  };

  return (
    <div className="control-pane">
      <div className="control-section">
        <h2>{t("ui.transactions.title")}</h2>

        <div style={{ marginBottom: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ minWidth: 110, margin: 0 }}>
              {t("ui.transactions.chargeDate")}:
            </label>
            <span>{t("ui.transactions.from")}</span>
            <DatePicker
              placeholder={t("ui.transactions.chargeDateFrom")}
              value={chargeFromDate}
              onChange={(v) => setChargeFromDate(v)}
              allowClear
            />
            <span>{t("ui.transactions.to")}</span>
            <DatePicker
              placeholder={t("ui.transactions.chargeDateTo")}
              value={chargeToDate}
              onChange={(v) => setChargeToDate(v)}
              allowClear
              disabledDate={(current) =>
                chargeFromDate
                  ? current && current.isBefore(chargeFromDate, "day")
                  : false
              }
            />
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ minWidth: 110, margin: 0 }}>
              {t("ui.transactions.createDate")}:
            </label>
            <span>{t("ui.transactions.from")}</span>
            <DatePicker
              placeholder={t("ui.transactions.createDateFrom")}
              value={createFromDate}
              onChange={(v) => setCreateFromDate(v)}
              allowClear
            />
            <span>{t("ui.transactions.to")}</span>
            <DatePicker
              placeholder={t("ui.transactions.createDateTo")}
              value={createToDate}
              onChange={(v) => setCreateToDate(v)}
              allowClear
              disabledDate={(current) =>
                createFromDate
                  ? current && current.isBefore(createFromDate, "day")
                  : false
              }
            />
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ minWidth: 110, margin: 0 }}>
              {t("ui.transactions.filterByCustomer")}:
            </label>
            <Select
              placeholder={t("ui.transactions.selectCustomer")}
              style={{ width: 280 }}
              value={selectedCustomer}
              onChange={(v) => setSelectedCustomer(v)}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {customers?.map((c) => (
                <Option key={c.number} value={c.number}>
                  {c.number} - {c.name}
                </Option>
              ))}
            </Select>
            <Button
              type="primary"
              onClick={handleSearch}
              loading={loading}
              disabled={!transactions?.length}
            >
              {t("ui.transactions.search")}
            </Button>
            <Button
              onClick={handleExportRivhit}
              disabled={!gridData?.length}
            >
              {t("ui.transactions.exportToRivhit")}
            </Button>
            <Button
              onClick={handleBuildMasav}
              loading={loading}
              disabled={!gridData?.length}
            >
              {t("ui.transactions.buildMasav")}
            </Button>
            <Button
              onClick={handlePrintSelected}
              disabled={!selectedCount}
            >
              {t("ui.transactions.printSelected")}
            </Button>
          </div>
        </div>

        {selectedCount > 0 && (
          <div
            style={{
              marginBottom: 8,
              color: "#1677ff",
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            {t("ui.transactions.selectedMovesSummary", {
              count: selectedCount,
              sum: selectedSum.toLocaleString("he-IL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            })}
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <Checkbox
            checked={useExternNumber}
            onChange={(e) => setUseExternNumber(e.target.checked)}
          >
            {t("ui.transactions.useExternNumberInMasav")}
          </Checkbox>
        </div>

        <div style={{ width: "100%", overflowX: "auto" }}>
        <GridComponent
          ref={gridRef}
          dataSource={gridData}
          width="100%"
          allowSorting={true}
          allowPaging={true}
          pageSettings={{ pageSize: 20 }}
          loading={loading}
          enableRtl={true}
          allowKeyboard={true}
          selectionSettings={{ type: "Multiple", checkboxOnly: true, persistSelection: true }}
          rowSelected={handleSelectionChange}
          rowDeselected={handleSelectionChange}
          checkBoxChange={handleSelectionChange}
          sortSettings={{
            columns: [{ field: "create_date", direction: "Descending" }],
          }}
          editSettings={{
            allowEditing: false,
            allowAdding: false,
            allowDeleting: false,
            mode: "Batch",
          }}
          allowTextWrap={true}
          textWrapSettings={{ wrapMode: "Header" }}
          allowFiltering={true}
          filterSettings={{ type: "Excel" }}
        >
          <ColumnsDirective>
            <ColumnDirective type="checkbox" width="30" />
            <ColumnDirective
              field="asc_num"
              headerText={t("ui.transactions.asmakhta")}
              width="120"
              textAlign="Center"
              isPrimaryKey={true}
            />
            <ColumnDirective
              field="customer"
              headerText={t("ui.transactions.customerNumber")}
              width="120"
              textAlign="Center"
              allowEditing={false}
            />
            <ColumnDirective
              field="cust_extern"
              headerText={t("ui.customers.externNumber")}
              width="120"
              textAlign="Center"
              type="number"
              allowEditing={false}
            />
            <ColumnDirective
              field="name"
              headerText={t("ui.transactions.name")}
              width="140"
              textAlign="Center"
              allowEditing={false}
            />
            <ColumnDirective
              field="charge_sum"
              headerText={t("ui.transactions.chargeSum")}
              width="120"
              textAlign="Center"
              type="number"
              format="C2"
              editType="numericedit"
            />
            <ColumnDirective
              field="period"
              headerText={t("ui.transactions.period")}
              width="120"
              textAlign="Center"
              allowEditing={false}
            />
            <ColumnDirective
              field="charge_date"
              headerText={t("ui.transactions.chargeDate")}
              width="130"
              textAlign="Center"
              allowEditing={false}
              valueAccessor={dateValueAccessor}
              sortComparer={compareDate}
            />
            <ColumnDirective
              field="create_date"
              headerText={t("ui.transactions.createDate")}
              width="130"
              textAlign="Center"
              allowEditing={false}
              valueAccessor={dateValueAccessor}
              sortComparer={compareDate}
            />
            <ColumnDirective
              field="note"
              headerText={t("ui.transactions.note")}
              width="200"
              textAlign="Center"
              allowEditing={false}
              wrapMode="Both"
            />
          </ColumnsDirective>
          <Inject services={[Page, Toolbar, Filter, Sort, Selection]} />
        </GridComponent>
        </div>

        <Modal
          title={t("ui.transactions.exportToRivhit")}
          open={rivhitModalOpen}
          onCancel={() => setRivhitModalOpen(false)}
          onOk={handleConfirmRivhitExport}
          okText={t("ui.transactions.exportToRivhit")}
          cancelText={t("common.cancel")}
          destroyOnClose
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>
                {t("ui.transactions.rivhitDueDate")}
              </div>
              <Radio.Group
                value={dueDateMode}
                onChange={(e) => setDueDateMode(e.target.value)}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <Radio value="charge_date">
                  {t("ui.transactions.rivhitDueDateCharge")}
                </Radio>
                <Radio value="custom">
                  <Space>
                    {t("ui.transactions.rivhitDueDateCustom")}
                    <DatePicker
                      value={customDueDate}
                      onChange={(v) => setCustomDueDate(v)}
                      disabled={dueDateMode !== "custom"}
                      allowClear={false}
                    />
                  </Space>
                </Radio>
              </Radio.Group>
            </div>

            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>
                {t("ui.transactions.rivhitSortCode")}
              </div>
              <InputNumber
                min={100}
                max={999}
                value={sortCode}
                onChange={(v) => setSortCode(v)}
                style={{ width: 140 }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10, marginTop: 10 }}>
              <Checkbox
                checked={includeVat}
                onChange={(e) => setIncludeVat(e.target.checked)}
              >
                {t("ui.transactions.rivhitIncludeVat")}
              </Checkbox>
              <span>{t("ui.transactions.rivhitVatPercent")}</span>
              <InputNumber
                min={0}
                max={100}
                value={vatPercent}
                onChange={(v) => setVatPercent(v)}
                disabled={includeVat}
                style={{ width: 90 }}
                addonAfter="%"
              />
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default withMosadIsolation(Transactions);
